import React, { useEffect, useState } from 'react';
import { App, Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { OWNER_ROLE_LABEL, parseJsonArray, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;

interface Props {
  open: boolean;
  template?: any | null;
  onClose: () => void;
}

/** Onboarding checklist master (popup allowed for masters). Due offsets are days from the join date. */
const OnboardingTemplateModal: React.FC<Props> = ({ open, template, onClose }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: positions = [] } = useQuery({ queryKey: hrmsKeys.positions(), queryFn: () => hrmsService.listPositions(), enabled: open });
  const { data: meta } = useQuery({ queryKey: hrmsKeys.onboardingMeta(), queryFn: () => hrmsService.getOnboardingMeta(), enabled: open });
  const isDefault = template?.template_code === 'DEFAULT';

  useEffect(() => {
    if (!open) return;
    if (template) {
      form.setFieldsValue({ ...template, tasks_json: parseJsonArray(template.tasks_json) });
    } else {
      form.resetFields();
      form.setFieldsValue({
        is_active: true,
        tasks_json: [{ task_type: 'Checklist', owner_role: 'HR', due_offset_days: 0, is_mandatory: true }],
      });
    }
  }, [open, template, form]);

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (template) await hrmsService.updateOnboardingTemplate(template.id, v);
      else await hrmsService.createOnboardingTemplate(v);
      message.success(template ? 'Template updated' : 'Template created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboardingTemplates() });
      onClose();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const roles = meta?.owner_roles?.length ? meta.owner_roles : Object.keys(OWNER_ROLE_LABEL);
  const types = meta?.task_types?.length ? meta.task_types : ['Checklist', 'Document', 'Acknowledgement'];

  return (
    <Modal
      open={open}
      title={template ? `Edit ${template.template_name}` : 'New onboarding template'}
      okText="Save"
      onOk={save}
      confirmLoading={saving}
      onCancel={onClose}
      width={980}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={10}>
            <Form.Item name="template_name" label="Name" rules={[{ required: true, whitespace: true }]}>
              <Input placeholder="e.g. Site staff onboarding" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="employment_type" label="Employment type">
              <Select allowClear placeholder="Any" disabled={isDefault} options={['Full-time', 'Part-time', 'Contract', 'Intern'].map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="department_id" label="Department">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Any"
                disabled={isDefault}
                options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="position_id" label="Position">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Any"
                disabled={isDefault}
                options={positions.map((p: any) => ({ value: p.id, label: p.position_name || p.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch disabled={isDefault} />
            </Form.Item>
          </Col>
        </Row>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          The most specific active template is used automatically (position, then department, then employment type). Due offset is days from the join date — negative means before Day 1. Owners for each role are set in Careers settings; line-manager tasks go to the hire's manager.
        </Text>
        <Form.List
          name="tasks_json"
          rules={[{ validator: async (_, v) => (v?.length ? undefined : Promise.reject(new Error('Add at least one task'))) }]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              <Row gutter={8} style={{ fontWeight: 500, marginBottom: 4 }}>
                <Col span={9}>Task</Col>
                <Col span={4}>Type</Col>
                <Col span={4}>Owner</Col>
                <Col span={3}>Due (days)</Col>
                <Col span={3}>Mandatory</Col>
              </Row>
              {fields.map((f) => (
                <Row gutter={8} key={f.key}>
                  <Form.Item name={[f.name, 'task_code']} hidden>
                    <Input />
                  </Form.Item>
                  <Col span={9}>
                    <Form.Item name={[f.name, 'task_name']} rules={[{ required: true, whitespace: true, message: 'Required' }]}>
                      <Input placeholder="e.g. Safety induction" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item name={[f.name, 'task_type']}>
                      <Select options={types.map((t) => ({ value: t, label: t }))} />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item name={[f.name, 'owner_role']}>
                      <Select options={roles.map((r) => ({ value: r, label: OWNER_ROLE_LABEL[r] || r }))} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[f.name, 'due_offset_days']}>
                      <InputNumber min={-60} max={180} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[f.name, 'is_mandatory']} valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
                  </Col>
                </Row>
              ))}
              <Form.ErrorList errors={errors} />
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ task_type: 'Checklist', owner_role: 'HR', due_offset_days: 0, is_mandatory: true })}>
                Add task
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default OnboardingTemplateModal;
